/**
 * Доля хвоста публичного рендерера: вид `close_webview_button` (кнопка
 * «закрыть встроенное окно»).
 *
 * WEB-2920, заход 3: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с компонентом кнопки (`CloseWebviewButton`); хук перепроверки
 * моста берётся у доли `RestorePurchasesRenderer.tsx`, где он стоял рядом со
 * ссылкой восстановления. Метка `case 'close_webview_button'` остаётся в
 * фасаде; ветка вида — функция отрисовки `renderCloseWebviewButton(ctx)`,
 * стиль кнопки и ОБЩИЙ цвет текста воронки приходят объектом-контекстом
 * (`ExtendedRenderContext`). Эту долю импортирует только фасад: она едет в
 * ленивом куске `extended`, а не в первом экране (самопроверка обхода).
 */
import type React from 'react';
import { buttonSurfaceStyle } from '../blockBackgroundStyle';
import type { EditorBlock } from '@/store/editorSlice';
import type { VariableContext } from '@/player/variable-context';
import { RichTextContent } from '../RichTextContent';
import { asRichField } from '@/types/rich-text';
import { appLinkHref } from '@/utils/appLinkHref';
import { primaryButtonStyle } from '@/themes';
import type { ButtonStyle } from '@/themes/theme.types';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { postToWeb2AppBridge } from '@/utils/web2appBridge';
import { usePostPurchaseContext } from '@/components/paywall-runtime/PostPurchaseContext';
import { pickStrings } from '@/i18n';
// WEB-2057: подсказки АВТОРУ — из своего маленького словаря. Полный словарь
// конструктора этому файлу нельзя: его рисует ПУБЛИЧНАЯ страница покупателя.
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { AuthorHintBox } from '../AuthorHintBox';
import { useHasWeb2AppBridge } from './RestorePurchasesRenderer';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

/**
 * WEB-1212 — «закрыть встроенное окно»: авторская кнопка на экране пейволла
 * (типично — после оплаты). Внутри контейнера SDK шлёт мосту РОВНО
 * {event: 'close'} — то же терминальное событие, что уже понимает
 * BridgeEventParser (web2appBridge.ts); SDK закрывает WebView, интегратор
 * получает PaywallResult. Вне контейнера закрывать нечего: своя ссылка
 * (block.url) заменяет действие обычным переходом; без ссылки кнопка скрыта
 * вовсе — мёртвый клик хуже пустоты (тот же принцип, что у WEB-853/WEB-1414).
 *
 * КАКАЯ ССЫЛКА ГОДИТСЯ — по факту, а не «тот же allowlist, что у
 * button_link» (так было написано раньше, и это враньё: у button_link дверь
 * ШИРЕ). Дверь здесь одна — `utils/appLinkHref`: http(s) через общий
 * `safeHref` ЛИБО схема приложения ЭТОГО проекта. Схему приложения даёт
 * `PostPurchaseContext` (`appSchemeUrl`, WEB-800), а он есть только на экране
 * ПОСЛЕ ОПЛАТЫ. Правило двери (свежий взгляд по пачке 18): схема в контексте
 * ЕСТЬ → своя ссылка проходит только при точном совпадении схемы; схемы в
 * контексте НЕТ (обычный экран, либо у проекта не настроена returnScheme) →
 * проходит любая НЕОПАСНАЯ схема, как было до WEB-1911, — отбрасывать её
 * значило бы молча убить кнопку у проектов с возвратом Universal Link'ами.
 * Маркер `w2a://store` не проходит ни при каком контексте: подменять его
 * настоящим адресом умеет одна ветка (button_link), и эта кнопка — не она.
 *
 * Превью конструктора не обещает больше публичной страницы (ревизия пачки
 * 19): кнопка на канве видна всегда (иначе блок не выделить и не настроить),
 * но если автор ссылку ЗАДАЛ, а дверь её не пропустила, рядом встаёт
 * подсказка АВТОРУ — «работает только внутри приложения». Тем же приёмом и
 * тем же `AuthorHintBox`, что у кнопки в магазин без адреса (WEB-1906).
 * Ссылки нет вовсе — подсказки нет: это штатный «мост-онли» вид кнопки,
 * автор ничего не просил, а плашка на каждой такой кнопке была бы шумом.
 * Рендерится <button>, не <a> — навигация вне моста делается вручную
 * (window.location.href), тот же выбор, что у RestorePurchasesLink, и он же
 * избегает пустого href на <a> (недоступной с клавиатуры ссылки), когда ни
 * моста, ни своей ссылки нет.
 *
 * Гонка инициализации моста — переиспользует useHasWeb2AppBridge
 * (пачка 13, RestorePurchasesLink): SDK может вставить объект моста в window
 * ПОСЛЕ первого рендера страницы.
 */
function CloseWebviewButton({
  block,
  buttonStyle,
  textColor,
  varCtx,
}: {
  block: EditorBlock;
  buttonStyle?: ButtonStyle;
  textColor?: string;
  varCtx?: VariableContext;
}): React.ReactElement | null {
  const { isEditorPreview } = useEditorPreview();
  const hasBridge = useHasWeb2AppBridge();
  // WEB-1911: своя ссылка автора чистится той же дверью, что у кнопки
  // возврата (utils/appLinkHref): разрешены http(s) и схема приложения ЭТОГО
  // проекта, маркер `w2a://store` и прочее — нет. Негодный адрес = адреса
  // нет, и правило видимости выше само уводит кнопку с экрана: мёртвый клик
  // хуже пустоты (WEB-1212).
  const { appSchemeUrl } = usePostPurchaseContext();
  const fallbackUrl = appLinkHref(block.url, appSchemeUrl);

  // Вне контейнера и без своей ссылки — рисовать нечего (см. коммент выше).
  if (!isEditorPreview && !hasBridge && !fallbackUrl) return null;

  // Подсказка АВТОРУ ровно в том случае, когда он ссылку ЗАДАЛ, а она здесь
  // адресом не стала (типично — схема, не совпавшая с настроенной схемой
  // проекта, либо служебный маркер): публично в браузере кнопки не будет, а
  // превью её рисует. Показывается ТОЛЬКО на канве — покупателю служебных плашек не
  // носим. Граница: ссылки нет вовсе — подсказки нет, это штатный
  // «мост-онли» вид кнопки по контракту WEB-1212, автор ничего не просил.
  const hasOwnUrl =
    typeof block.url === 'string' && block.url.trim().length > 0;
  const showAuthorHint = isEditorPreview && hasOwnUrl && !fallbackUrl;

  return (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <button
        type="button"
        data-testid={`close-webview-button-${block.id}`}
        onClick={() => {
          // В превью конструктора клик только выделяет блок (всплытие),
          // бридж-событий/навигации не шлём — боевой путь ниже.
          if (isEditorPreview) return;
          const posted = postToWeb2AppBridge({ event: 'close' });
          if (posted) return; // мост закрывает контейнер, ссылка не нужна
          if (fallbackUrl) window.location.href = fallbackUrl;
        }}
        style={{
          ...primaryButtonStyle(buttonStyle, textColor),
          width: '100%',
          // WEB-1651-2: тот же общий хелпер приоритетов, что у остальных
          // кнопочных мест (фон кнопки / заливка элемента / цвет текста).
          ...buttonSurfaceStyle(block),
          ...(block.fontSize ? { fontSize: block.fontSize } : {}),
        }}
      >
        <RichTextContent
          value={asRichField(block.buttonText)}
          varCtx={varCtx}
          fallback={getPublicQuizStrings(varCtx?.system?.locale).closeLabel}
        />
      </button>
      {showAuthorHint && (
        <AuthorHintBox
          testId={`close-webview-only-in-app-${block.id}`}
          compact
          style={{
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          {pickStrings(authorHintsStrings).closeWebviewOnlyInApp}
        </AuthorHintBox>
      )}
    </div>
  );
}

/** Ветка `close_webview_button` фасада — тело `case` слово в слово. */
export function renderCloseWebviewButton(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, textColor, buttonStyle } = ctx;
  return (
    <CloseWebviewButton
      block={block}
      buttonStyle={buttonStyle}
      textColor={textColor}
      varCtx={varCtx}
    />
  );
}
